import { db } from "../firebase/config";
import { doc, getDoc, runTransaction } from "firebase/firestore";
import { validateContent } from "../utils/contentValidation";
import { backupService } from "./backupService";

const HOME_CONTENT_COLLECTION = "HomeContent";
const SECTIONS_DOC = "Sections";

export const homeContentService = {
  async getAllSections() {
    const docRef = doc(db, HOME_CONTENT_COLLECTION, SECTIONS_DOC);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  },

  async updateSectionOrder(updates) {
    const docRef = doc(db, HOME_CONTENT_COLLECTION, SECTIONS_DOC);

    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      if (!docSnap.exists()) {
        throw new Error("Sections document not found");
      }

      const data = docSnap.data();
      const sections = [...data.sections];

      if (!Array.isArray(updates)) {
        throw new Error("Updates must be an array");
      }

      updates.forEach((update) => {
        const sectionIndex = sections.findIndex((s) => s.name === update.name);
        if (sectionIndex !== -1) {
          sections[sectionIndex] = {
            ...sections[sectionIndex],
            position: update.position,
          };
        }
      });

      const { isValid, errors } = validateContent({ sections });
      if (!isValid) {
        throw new Error(`Validation failed: ${errors.join(", ")}`);
      }

      transaction.update(docRef, {
        sections: sections,
        lastUpdated: new Date().toISOString(),
      });
    });
  },

  async updateSection(sectionId, data, parentSectionId = null) {
    const docRef = doc(db, HOME_CONTENT_COLLECTION, SECTIONS_DOC);

    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      if (!docSnap.exists()) {
        throw new Error("Document does not exist!");
      }

      const currentData = docSnap.data();

      let parentSection;
      if (parentSectionId) {
        parentSection = currentData.sections.find(
          (section) => section.name === parentSectionId
        );
      } else {
        parentSection = currentData.sections.find((section) =>
          section.sections?.some((s) => s.id === sectionId)
        );
      }

      if (!parentSection) {
        throw new Error("Parent section not found");
      }

      const sectionIndex = parentSection.sections.findIndex(
        (s) => s.id === sectionId
      );
      if (sectionIndex === -1) {
        throw new Error(`Section ${sectionId} not found`);
      }

      await backupService.createBackup(
        sectionId,
        parentSection.name,
        parentSection.sections[sectionIndex]
      );

      parentSection.sections[sectionIndex] = {
        ...parentSection.sections[sectionIndex],
        ...data,
        lastUpdated: new Date().toISOString(),
      };

      transaction.update(docRef, {
        sections: currentData.sections,
      });
    });

    return true;
  },

  async addPage(name, title, sections) {
    const docRef = doc(db, HOME_CONTENT_COLLECTION, SECTIONS_DOC);

    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(docRef);
      const currentData = docSnap.exists()
        ? docSnap.data()
        : { sections: [] };

      const { isValid, errors } = validateContent({ sections });
      if (!isValid) {
        throw new Error(`Validation failed: ${errors.join(", ")}`);
      }

      const newSection = {
        name,
        title,
        sections,
        active: true,
        lastUpdated: new Date().toISOString(),
      };

      const updatedSections = [...currentData.sections, newSection];

      transaction.set(docRef, {
        sections: updatedSections,
        lastUpdated: new Date().toISOString(),
      });
    });

    return true;
  },

  async rollbackToBackup(backupId) {
    const backup = await backupService.getBackup(backupId);
    if (!backup) {
      throw new Error("Backup not found");
    }

    const rollbackData = {
      ...backup.data,
      lastUpdated: new Date().toISOString(),
    };

    await this.updateSection(
      backup.sectionId,
      rollbackData,
      backup.parentSectionId
    );

    return true;
  },
};
